import { MissingItemsWarning } from '@/components/layout/ModalConfirmDelivery/MissingItemsWarning';
import { UIBox } from '@/components/ui/Box';
import { UIButton } from '@/components/ui/Button';
import ErrorButton from '@/components/ui/Button/ErrorButton';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import { UIModal } from '@/components/ui/Modal';
import { Typography } from '@/components/ui/Typography';
import { ReceivingProcess as ReceivingProcessType, ReceivingProcessConst } from '@/features/deliveries/deliveriesSlice';
import { useSelector } from '@/hooks/useSelector';
import { palette } from '@/theme';
import { FormControl, IconButton, Radio, RadioGroup } from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { TFunction } from 'i18next';
import { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

//#region - Styled Components
const StyledWrapper = styled(UIBox)`
  width: 100%;
  padding: 24px;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const StyledCloseIcon = styled(IconButton)`
  position: absolute;
  top: 12px;
  right: 12px;
  align-self: baseline;

  svg {
    fill: ${palette.colors.primary};
  }
`;

const StyledContent = styled(UIBox)`
  flex-direction: column;
  width: 100%;
`;

const StyledMessageWrapper = styled(UIBox)`
  display: flex;
  flex-direction: column;
  margin: 18px 0 32px;
  gap: 24px;

  span {
    line-height: 22px !important;
  }
`;

const StyledButtonContainer = styled(UIBox)`
  align-items: center;
  gap: 43px;
  width: 60%;
  align-self: flex-end;
`;

const StyledRadioWrapper = styled(UIBox)`
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  gap: 8px;
`;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
//#endregion

type ReceivingProcess = ReceivingProcessType | undefined;

export interface ModalConfirmDeliveryProps {
  open: boolean;
  onConfirmClick: (currentFlow: ReceivingProcessType) => Promise<void> | void;
  onUnconfirmClick?: () => Promise<void> | void;
  onClose: () => void;
  disableConfirm?: boolean;
  loadingConfirm?: boolean;
}

const radioButtons = (
  t: TFunction
): {
  label: string;
  subtitle: string;
  value: ReceivingProcess;
}[] => [
  {
    label: t('receiving.close.label.partialReceiving'),
    subtitle: t('receiving.close.label.partialReceiving.subtitle'),
    value: ReceivingProcessConst.RCP,
  },
  {
    label: t('receiving.close.label.fullReceiving'),
    subtitle: t('receiving.close.label.fullReceiving.subtitle'),
    value: ReceivingProcessConst.RCC,
  },
];

const ReceivingProcessSelector = ({
  value,
  onChange,
}: {
  value: ReceivingProcess;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}): JSX.Element => {
  const { t } = useTranslation();

  const options = useMemo(() => radioButtons(t), [t]);

  return (
    <FormControl>
      <StyledRadioGroup
        aria-labelledby="receiving-process"
        name="receiving-process"
        value={value}
        onChange={onChange}
      >
        {options.map(radio => (
          <FormControlLabel
            key={radio.value}
            value={radio.value}
            control={<Radio color="primary" />}
            label={
              <StyledRadioWrapper>
                <Typography>{radio.label}</Typography>
                <Typography font="light">{radio.subtitle}</Typography>
              </StyledRadioWrapper>
            }
          />
        ))}
      </StyledRadioGroup>
    </FormControl>
  );
};

const MissingItemsAcknowledgement = ({
  missingQty,
  acknowledge,
  onChange,
}: {
  missingQty: number;
  acknowledge: boolean;
  onChange: () => void;
}): JSX.Element | null => {
  const { t } = useTranslation();

  if (missingQty <= 0) return null;

  return (
    <>
      <MissingItemsWarning missingQty={missingQty} />
      <FormControlLabel
        control={<Checkbox checked={acknowledge} onChange={onChange} />}
        label={t('receiving.confirmModal.check')}
      />
    </>
  );
};

const ModalConfirmDelivery: FC<ModalConfirmDeliveryProps> = ({
  open,
  onClose,
  onConfirmClick,
  onUnconfirmClick,
  disableConfirm,
  loadingConfirm,
}) => {
  const { t } = useTranslation();

  const { deliveryProcess } = useSelector(state => state.deliveries);
  const { products } = useSelector(state => state.scannedProducts);

  const [acknowledge, setAcknowledge] = useState(false);
  const [currentFlow, setCurrentFlow] =
    useState<ReceivingProcess>(deliveryProcess);

  const missingQty = products?.missing?.qty ?? 0;
  const isPartialReceiving = deliveryProcess === ReceivingProcessConst.RCP;
  const isFullReceiving = deliveryProcess === ReceivingProcessConst.RCC;

  const modalTitle = isFullReceiving
    ? t('receiving.confirmModal.title')
    : isPartialReceiving
    ? t('receiving.close.partialReceiving')
    : t('payAttention');

  const modalSubtitle = isFullReceiving
    ? ''
    : isPartialReceiving
    ? t('receiving.close.partialReceivingSubtitle')
    : '';

  const primaryButtonLabel = isFullReceiving ? t('yes') : t('confirm');
  const secondaryButtonLabel = isFullReceiving ? t('no') : t('close');

  const handleChangeReceivingProcess = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setCurrentFlow(event.target.value as ReceivingProcess);
      if ((event.target.value as ReceivingProcess) === ReceivingProcessConst.RCP) {
        setAcknowledge(false);
      }
    },
    []
  );

  const handleAcknowledgeToggle = useCallback((): void => {
    setAcknowledge(prev => !prev);
  }, []);

  const handleSecondaryClick = onUnconfirmClick || onClose;

  return (
    <UIModal
      $minWidth="500px"
      $minHeight="225px"
      $maxWidth="650px"
      open={open}
      onClose={onClose}
    >
      <StyledWrapper>
        <StyledContent>
          <Typography font="heavy">{modalTitle}</Typography>
          <Typography font="medium" style={{ paddingTop: '8px' }}>
            {modalSubtitle}
          </Typography>

          <StyledMessageWrapper>
            {isFullReceiving && (
              <>
                <Typography font="medium" size="md">
                  {t('receiving.confirmModal.message')}
                </Typography>

                <MissingItemsAcknowledgement
                  missingQty={missingQty}
                  acknowledge={acknowledge}
                  onChange={handleAcknowledgeToggle}
                />

                <Typography font="heavy" size="md">
                  {t('receiving.confirmModal.confirm')}
                </Typography>
              </>
            )}

            {isPartialReceiving && (
              <>
                <ReceivingProcessSelector
                  value={currentFlow}
                  onChange={handleChangeReceivingProcess}
                />

                {currentFlow === ReceivingProcessConst.RCC && (
                  <MissingItemsAcknowledgement
                    missingQty={missingQty}
                    acknowledge={acknowledge}
                    onChange={handleAcknowledgeToggle}
                  />
                )}

                <Typography font="light" size="sm">
                  {t('receiving.confirmModal.note')}
                </Typography>
              </>
            )}
          </StyledMessageWrapper>

          <StyledButtonContainer>
            <ErrorButton
              label={secondaryButtonLabel}
              onClick={handleSecondaryClick}
              errorStyle
              outlined
            />
            <UIButton
              label={primaryButtonLabel}
              loading={loadingConfirm}
              onClick={(): Promise<void> | void =>
                currentFlow && onConfirmClick(currentFlow)
              }
              disabled={
                disableConfirm ||
                (currentFlow === ReceivingProcessConst.RCC &&
                  missingQty > 0 &&
                  !acknowledge)
              }
            />
          </StyledButtonContainer>
        </StyledContent>

        <StyledCloseIcon onClick={onClose}>
          <HighlightOffIcon />
        </StyledCloseIcon>
      </StyledWrapper>
    </UIModal>
  );
};

export default ModalConfirmDelivery;
